/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';

import ErrorName from '../error/error-name';
import NetworkType from '../network/network-type';

// TODO Rozšířit i o infomace přidané v Device#getReportInfo
// - Bude možné až po migraci všech platforem na multiplatformní Adapter.
// - Přidá položky: buildId, contentTimestamp, version.
/**
 * Správce informací pro reportování.
 * @extends EventBus
 */
class ReportManager {
  /**
   * Vytvoří instanci.
   * @param {AppManager} appManager Správce aplikace.
   * @param {ControlManager} controlManager Správce ovládání.
   * @param {DriveManager} driveManager Správce fyzických datových úložišť.
   * @param {FirmwareManager} firmwareManager Správce firmware.
   * @param {Logger} logger
   * @param {MemoryManager} memoryManager Správce operační paměti.
   * @param {NetworkManager} networkManager Správce připojení k síti.
   * @param {PowerManager} powerManager Správce napájení.
   * @param {ScreenManager} screenManager Správce obrazovky.
   * @param {SensorManager} sensorManager Správce fyzických senzorů.
   * @param {SoundManager} soundManager Správce zvuku.
   * @param {TimeManager} timeManager Správce času a časových zón.
   * @returns {ReportManager} Nová instance.
   */
  constructor(
    appManager,
    controlManager,
    driveManager,
    firmwareManager,
    logger,
    memoryManager,
    networkManager,
    powerManager,
    screenManager,
    sensorManager,
    soundManager,
    timeManager
  ) {
    this.appManager = appManager;
    this.controlManager = controlManager;
    this.driveManager = driveManager;
    this.firmwareManager = firmwareManager;
    this.logger = logger;
    this.memoryManager = memoryManager;
    this.networkManager = networkManager;
    this.powerManager = powerManager;
    this.screenManager = screenManager;
    this.sensorManager = sensorManager;
    this.soundManager = soundManager;
    this.timeManager = timeManager;
  }

  /**
   * Zjistí část informací s časem zapnutí zařízení.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getBootTimestamp() {
    return new Promise((resolve) =>
      resolve(
        this.powerManager
          .getBootDate()
          .then((date) => ({ bootTimestamp: date.toISOString() }))
      )
    );
  }

  /**
   * Zjistí část informací s jasem obrazovky.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getBrightness() {
    return new Promise((resolve) =>
      resolve(
        this.screenManager
          .getBrightness()
          .then((brightness) => ({ brightness }))
      )
    );
  }

  /**
   * Zjistí část informací s fyzickými datovými úložišťi.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getDriveInfo() {
    return new Promise((resolve) =>
      resolve(this.driveManager.getInfo().then((driveInfo) => ({ driveInfo })))
    );
  }

  /**
   * Zjistí část informací s verzí firmware.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getFirmwareVersion() {
    return new Promise((resolve) =>
      resolve(
        this.firmwareManager
          .getVersion()
          .then((firmwareVersion) => ({ firmwareVersion }))
      )
    );
  }

  /**
   * Zjistí část informací se snímkem obrazovky.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getImage() {
    return new Promise((resolve) =>
      resolve(
        this.screenManager.createScreenshot().then((image) => ({ image }))
      )
    );
  }

  /**
   * Zjistí informace pro reportování.
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getInfo() {
    return new Promise((resolve) =>
      resolve(
        [
          () => this.getBootTimestamp(),
          () => this.getBrightness(),
          () => this.getDriveInfo(),
          () => this.getFirmwareVersion(),
          () => this.getImage(),
          () => this.getLocalControlLock(),
          () => this.getManufacturer(),
          () => this.getMemory(),
          () => this.getModelName(),
          () => this.getMuted(),
          () => this.getNetwork(),
          () => this.getNowTimestamp(),
          () => this.getOrientation(),
          () => this.getRemoteControlLock(),
          () => this.getRuntimePlatform(),
          () => this.getSerialNumber(),
          () => this.getTemperature(),
          () => this.getTimezone(),
          () => this.getUpdatePlatform(),
          () => this.getUpdateUrl(),
          () => this.getVolume()
        ].reduce(
          (accPromise, getter) =>
            accPromise.then((accObject) =>
              getter()
                .then((result) => Object.assign(accObject, result))
                .catch((error) => {
                  // Chyby vycházející z nedostupnosti informací se ignorují.
                  if (error.name !== ErrorName.NotSupportedError) {
                    this.logger.error(error);
                  }
                  return accObject;
                })
            ),
          Promise.resolve({})
        )
      )
    );
  }

  /**
   * Zjistí část informací se zámkem ovládání pro tlačítka umístěné
   * přímo na zařízení.
   * @private
   * @returns {Promise.<ReportInfo>}
   */
  getLocalControlLock() {
    return new Promise((resolve) =>
      resolve(
        this.controlManager
          .getLocalLock()
          .then((lock) => ({ localControlLock: lock }))
      )
    );
  }

  /**
   * Zjistí část informací s výrobcem zařízení.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getManufacturer() {
    return new Promise((resolve) =>
      resolve(
        this.firmwareManager
          .getManufacturer()
          .then((manufacturer) => ({ manufacturer }))
      )
    );
  }

  /**
   * Zjistí část informací s operační pamětí.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getMemory() {
    return new Promise((resolve) =>
      resolve(
        this.memoryManager.getInfo().then((memoryInfo) => ({
          totalRam: memoryInfo.total,
          usedRam: memoryInfo.used
        }))
      )
    );
  }

  /**
   * Zjistí část informací s modelem zařízení.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getModelName() {
    return new Promise((resolve) =>
      resolve(
        this.firmwareManager.getModelName().then((modelName) => ({ modelName }))
      )
    );
  }

  /**
   * Zjistí část informací s vypnutím zvuku.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getMuted() {
    return new Promise((resolve) =>
      resolve(this.soundManager.isMuted().then((muted) => ({ muted })))
    );
  }

  /**
   * Zjistí část informací s připojením k síti.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getNetwork() {
    return new Promise((resolve) =>
      resolve(
        this.networkManager.getAllConnections().then((connectionArray) => {
          const wiredInfo = this.getWired(connectionArray);
          const wirelessInfo = this.getWireless(connectionArray);
          return { ...wiredInfo, ...wirelessInfo };
        })
      )
    );
  }

  /**
   * Zjistí část informací s aktuálním časem.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getNowTimestamp() {
    return new Promise((resolve) =>
      resolve({
        nowTimestamp: new Date().toISOString()
      })
    );
  }

  /**
   * Zjistí část informací s orientací obrazovky.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getOrientation() {
    return new Promise((resolve) =>
      resolve(
        this.screenManager
          .getOrientation()
          .then((orientationEnum) => ({ orientationEnum }))
      )
    );
  }

  /**
   * Zjistí část informací se zámkem ovládání pro tlačítka na dálkovém ovládání.
   * @private
   * @returns {Promise.<ReportInfo>}
   */
  getRemoteControlLock() {
    return new Promise((resolve) =>
      resolve(
        this.controlManager
          .getRemoteLock()
          .then((lock) => ({ remoteControlLock: lock }))
      )
    );
  }

  /**
   * Zjistí identifikátor platformy, na které ja aplikace momentálně spuštěná.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getRuntimePlatform() {
    return new Promise((resolve) =>
      resolve(
        this.appManager
          .getRuntimePlatform()
          .then((runtimePlatform) => ({ runtimePlatform }))
      )
    );
  }

  /**
   * Zjistí část informací se sériovým číslem zařízení.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getSerialNumber() {
    return new Promise((resolve) =>
      resolve(
        this.firmwareManager
          .getSerialNumber()
          .then((serialNumber) => ({ serialNumber }))
      )
    );
  }

  /**
   * Zjistí část informací s teplotou zařízení.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getTemperature() {
    return new Promise((resolve) =>
      resolve(
        this.sensorManager
          .getTemperature()
          .then((temperature) => ({ temperature }))
      )
    );
  }

  /**
   * Zjistí část informací s časovou zónou.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getTimezone() {
    return new Promise((resolve) =>
      resolve(this.timeManager.getZone().then((timezone) => ({ timezone })))
    );
  }

  /**
   * Zjistí část informací s platformou pro aktualizace.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getUpdatePlatform() {
    return new Promise((resolve) =>
      resolve(
        this.appManager
          .getUpdatePlatform()
          .then((updatePlatform) => ({ updatePlatform }))
      )
    );
  }

  /**
   * Zjistí část informací s aktualizační URL.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getUpdateUrl() {
    return new Promise((resolve) =>
      resolve(
        this.appManager.getUpdateUrl().then((updateUrl) => ({ updateUrl }))
      )
    );
  }

  /**
   * Zjistí část informací s hlasitostí zvuku.
   * @private
   * @returns {Promise.<ReportInfo>} Informace.
   */
  getVolume() {
    return new Promise((resolve) =>
      resolve(this.soundManager.getVolume().then((volume) => ({ volume })))
    );
  }

  /**
   * Zjistí část informací s pevným připojením k síti.
   * @param {Array.<NetworkConnection>} connectionArray Seznam dostupných připojení.
   * @private
   * @returns {ReportInfo} Informace.
   */
  getWired(connectionArray) {
    const foundConnection = connectionArray.find(
      (connection) => connection.getType() === NetworkType.Ethernet
    );
    if (isNil(foundConnection)) {
      return {};
    }
    const wiredIpInfo = this.getWiredIp(foundConnection);
    return {
      ...wiredIpInfo,
      wiredMacAddress: foundConnection.getMac()
    };
  }

  /**
   * Zjistí část informací s IP adresou pevého připojení k síti.
   * @param {NetworkConnection} connection Připojení.
   * @private
   * @returns {ReportInfo} Informace.
   */
  getWiredIp(connection) {
    if (!connection.hasIp()) {
      return {};
    }
    return { wiredIpAddress: connection.getIp() };
  }

  /**
   * Zjistí část informací s bezdrátovým připojením k síti.
   * @param {Array.<NetworkConnection>} connectionArray Seznam dostupných připojení.
   * @private
   * @returns {ReportInfo} Informace.
   */
  getWireless(connectionArray) {
    const foundConnection = connectionArray.find(
      (connection) => connection.getType() === NetworkType.Wifi
    );
    if (isNil(foundConnection)) {
      return {};
    }
    const wirelessIpInfo = this.getWirelessIp(foundConnection);
    return {
      ...wirelessIpInfo,
      wirelessMacAddress: foundConnection.getMac()
    };
  }

  /**
   * Zjistí část informací s IP adresou bezdrátového připojení k síti.
   * @param {NetworkConnection} connection Připojení.
   * @private
   * @returns {ReportInfo} Informace.
   */
  getWirelessIp(connection) {
    if (!connection.hasIp()) {
      return {};
    }
    return { wirelessIpAddress: connection.getIp() };
  }
}

export default ReportManager;
